export const EDITORIAL_SITE_URL = "https://premierpharma.com";
export const EDITORIAL_INSIGHTS_URL = `${EDITORIAL_SITE_URL}/insights`;
export const EDITORIAL_DEFAULT_IMAGE = `${EDITORIAL_SITE_URL}/og-social-card.jpg`;

type DateValue = Date | string | null | undefined;

export type EditorialSeoPerson = {
  slug: string;
  name: string;
  role?: string | null;
  bio?: string | null;
  profileUrl?: string | null;
  avatarUrl?: string | null;
};

export type EditorialArticleSeoInput = {
  slug: string;
  title: string;
  excerpt: string;
  contentType?: string | null;
  canonicalUrl?: string | null;
  publishedAt?: DateValue;
  updatedAt?: DateValue;
  sources?: string[] | null;
  author: EditorialSeoPerson;
  reviewer?: EditorialSeoPerson | null;
  socialImage?: { url: string } | null;
};

export type EditorialCategorySeoInput = {
  slug: string;
  name: string;
  description?: string | null;
};

export function absoluteEditorialUrl(value: string | null | undefined, fallback: string): string {
  try {
    const url = new URL(value || fallback, EDITORIAL_SITE_URL);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : fallback;
  } catch {
    return fallback;
  }
}

function isoDate(value: DateValue): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function personJsonLd(person: EditorialSeoPerson) {
  return {
    "@type": "Person",
    name: person.name,
    url: absoluteEditorialUrl(person.profileUrl, `${EDITORIAL_SITE_URL}/authors/${person.slug}`),
    ...(person.role ? { jobTitle: person.role } : {}),
    ...(person.bio ? { description: person.bio } : {}),
    ...(person.avatarUrl ? { image: absoluteEditorialUrl(person.avatarUrl, EDITORIAL_DEFAULT_IMAGE) } : {}),
  };
}

/** Shared between server head injection and browser navigation. */
export function buildEditorialAuthorSeo(author: EditorialSeoPerson) {
  const canonical = `${EDITORIAL_SITE_URL}/authors/${author.slug}`;
  return {
    title: `${author.name} | Premier Pharma Insights`,
    description: author.bio || `${author.name}, contributor to Premier Pharma Insights.`,
    canonical,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      name: author.name,
      url: canonical,
      mainEntity: personJsonLd(author),
    },
  };
}

export function editorialArticleTitle(title: string): string {
  return title;
}

export function editorialCategoryTitle(name: string): string {
  return `${name} Insights`;
}

export function editorialCategoryNameFromSlug(slug: string): string {
  const knownNames: Record<string, string> = {
    guides: "Guides",
    "dscsa-compliance": "DSCSA compliance",
    "pharmacy-operations": "Pharmacy operations",
    "drug-supply": "Drug supply",
    "company-news": "Company news",
  };
  return knownNames[slug] || slug
    .split("-")
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");
}

export function buildEditorialArticleJsonLd(article: EditorialArticleSeoInput) {
  const canonical = absoluteEditorialUrl(
    article.canonicalUrl,
    `${EDITORIAL_INSIGHTS_URL}/${article.slug}`,
  );
  const image = article.socialImage
    ? absoluteEditorialUrl(article.socialImage.url, EDITORIAL_DEFAULT_IMAGE)
    : undefined;

  return {
    "@context": "https://schema.org",
    "@type": article.contentType === "company_news" || article.contentType === "press_mention"
      ? "NewsArticle"
      : "Article",
    headline: article.title,
    description: article.excerpt,
    url: canonical,
    datePublished: isoDate(article.publishedAt),
    dateModified: isoDate(article.updatedAt),
    author: personJsonLd(article.author),
    ...(article.reviewer ? { reviewedBy: personJsonLd(article.reviewer) } : {}),
    publisher: { "@type": "Organization", name: "Premier Pharma", url: EDITORIAL_SITE_URL },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    ...(image ? { image } : {}),
    ...(article.sources?.length ? { citation: article.sources } : {}),
  };
}

export function buildEditorialCategoryJsonLd(name: string, slug: string) {
  const canonical = `${EDITORIAL_INSIGHTS_URL}/${slug.toLowerCase()}`;
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: editorialCategoryTitle(name),
    url: canonical,
  };
}

export function buildEditorialCategorySeo(category: EditorialCategorySeoInput) {
  const canonical = `${EDITORIAL_INSIGHTS_URL}/${category.slug.toLowerCase()}`;
  const description = category.description?.trim() || `${category.name} insights from Premier Pharma.`;
  return {
    title: editorialCategoryTitle(category.name),
    description,
    canonical,
    jsonLd: buildEditorialCategoryJsonLd(category.name, category.slug),
    breadcrumbs: [
      { name: "Home", url: EDITORIAL_SITE_URL },
      { name: "Insights", url: EDITORIAL_INSIGHTS_URL },
      { name: category.name, url: canonical },
    ],
  };
}